import { createServerFn } from "@tanstack/react-start";
import { AD_STATUS } from "@/lib/ad-pricing";
import { getDefaultCache } from "@/lib/cloudflare-cache";

/**
 * A public sponsor placement. Carries no logo field: agentmrr resolves the
 * favicon from the destination host via `/api/logo?host=...` at render time.
 */
export type PublicAd = {
	description?: string;
	id: string;
	name: string;
	url: string;
};

export function isActive(ad: {
	expiresAt?: Date | string | null;
	status: number;
}) {
	if (ad.status !== AD_STATUS.ACTIVE) return false;
	if (!ad.expiresAt) return true;
	return new Date(ad.expiresAt).getTime() > Date.now();
}

/** The rotating sponsor pool: active paid placements. */
export type AdsData = PublicAd[];

const ADS_DATA_CACHE_TTL_SECONDS = 300;
const ADS_DATA_STALE_SECONDS = 1800;
let adsDataInflight: Promise<AdsData> | null = null;

async function cachedAdsData(resolve: () => Promise<AdsData>) {
	const cache = getDefaultCache();
	// Versioned key: bumping it drops entries cached under an old shape, which
	// would otherwise deserialize as a non-array and break every sponsor slot
	// until the TTL elapsed.
	const cacheKey = new Request("https://agentmrr.internal/cache/public-ads-v1");
	const cached = await cache?.match(cacheKey);
	if (cached) {
		const data = (await cached.json()) as AdsData;
		// Guard against a stale/poisoned shape; fall through to a fresh resolve.
		if (Array.isArray(data) && data.length > 0) return data;
	}

	if (adsDataInflight) return adsDataInflight;

	adsDataInflight = resolve().then(async (data) => {
		// Never cache an empty result: a transient DB failure returns [], and
		// caching it would blank every sponsor slot for the full TTL. Leaving it
		// uncached lets the next request self-heal.
		if (data.length > 0) {
			const response = new Response(JSON.stringify(data), {
				headers: {
					"Cache-Control": `public, max-age=${ADS_DATA_CACHE_TTL_SECONDS}, s-maxage=${ADS_DATA_CACHE_TTL_SECONDS}, stale-while-revalidate=${ADS_DATA_STALE_SECONDS}`,
					"Content-Type": "application/json; charset=utf-8",
				},
			});
			await cache?.put(cacheKey, response);
		}
		return data;
	});

	try {
		return await adsDataInflight;
	} finally {
		adsDataInflight = null;
	}
}

/**
 * Server function used to SSR the sponsor placements (leaderboard rows + profile
 * recommendations) so the markup ships fully rendered instead of hydrating
 * placeholders client-side.
 *
 * The DB read lives in `agentmrr-ads.server.ts` and is pulled in via dynamic
 * import so the admin InstantDB client (and `@/lib/auth`) never leak into the
 * browser bundle when this server function is imported from client code.
 */
export const getAdsData = createServerFn({ method: "GET" }).handler(
	async (): Promise<AdsData> => {
		return cachedAdsData(async () => {
			const { buildAdsData } = await import("@/lib/agentmrr-ads.server");
			return buildAdsData();
		});
	},
);
